// Error message display duration - matches the add to cart error in product-form.js
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

/**
 * A custom element for the "Buy now" button of a product form. It adds the selected variant and
 * quantity to the cart and goes straight to checkout.
 *
 * The button mirrors the form's add to cart button, which the product form and the variant picker
 * keep disabled until a complete, available variant is chosen. The add is posted directly rather than
 * through the product form so no cart update event fires, which would open the cart drawer on the way out.
 */
class BuyNowButton extends HTMLElement {
  /** @type {MutationObserver | undefined} */
  #observer;

  /** @type {number | undefined} */
  #errorTimeout;

  connectedCallback() {
    this.button?.addEventListener('click', this.#handleClick);

    const productForm = this.closest('product-form-component');
    if (!productForm) return;

    // Watch the whole form: variant changes can morph or replace the add to cart button.
    this.#observer = new MutationObserver(this.#syncState);
    this.#observer.observe(productForm, { subtree: true, attributes: true, attributeFilter: ['disabled'] });
    this.#syncState();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.button?.removeEventListener('click', this.#handleClick);
    clearTimeout(this.#errorTimeout);
  }

  /** @returns {HTMLButtonElement | null} */
  get button() {
    return this.querySelector('button');
  }

  /** @returns {HTMLButtonElement | null} */
  get #addToCartButton() {
    return this.closest('product-form-component')?.querySelector('[ref="addToCartButton"]') ?? null;
  }

  #syncState = () => {
    const { button } = this;
    if (!button || button.getAttribute('aria-busy') === 'true') return;

    const disabled = this.#addToCartButton?.disabled ?? false;
    if (button.disabled !== disabled) button.disabled = disabled;
  };

  #handleClick = async () => {
    const { button } = this;
    const form = this.closest('form');
    if (!button || !form || button.disabled || this.#addToCartButton?.disabled) return;
    if (!form.reportValidity()) return;

    button.setAttribute('aria-busy', 'true');
    button.disabled = true;

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || button.dataset.errorMessage);
      }

      window.location.assign(button.dataset.checkoutUrl || '/checkout');
    } catch (error) {
      this.#showError(error instanceof Error ? error.message : String(error));
      button.removeAttribute('aria-busy');
      this.#syncState();
    }
  };

  /**
   * Shows the message in the form's add to cart error slot, the same place add to cart errors appear.
   * @param {string} message
   */
  #showError(message) {
    const errorElement = this.closest('product-form-component')?.querySelector('[ref="addToCartTextError"]');
    if (!errorElement) return;

    // The message sits right after the error icon, where product-form.js writes it too.
    let textNode = errorElement.querySelector('.svg-wrapper')?.nextSibling;
    if (textNode?.nodeType !== Node.TEXT_NODE) {
      textNode = errorElement.appendChild(document.createTextNode(''));
    }
    textNode.textContent = message;
    errorElement.classList.remove('hidden');

    clearTimeout(this.#errorTimeout);
    this.#errorTimeout = setTimeout(() => errorElement.classList.add('hidden'), ERROR_MESSAGE_DISPLAY_DURATION);
  }
}

if (!customElements.get('buy-now-button')) {
  customElements.define('buy-now-button', BuyNowButton);
}
